import { useState, useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { dashboardApi } from '@/services/dashboard-api';
import { useLivePolling } from './useLivePolling';
import type {
  Dashboard,
  DashboardBlock,
  DashboardControl,
  RenderHandle,
  ControlValues,
} from '@/types/dashboard';

interface UseDashboardViewerReturn {
  dashboard: Dashboard | null;
  blocks: DashboardBlock[];
  blockData: Map<string, Record<string, unknown>[]>;
  controls: DashboardControl[];
  activeControlValues: Record<string, unknown>;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  renderHandles: React.MutableRefObject<Map<string, RenderHandle>>;
  changeControlValue: (parameterKey: string, value: unknown) => Promise<void>;
  refreshData: () => Promise<void>;
  lastRefreshedAt: Date | null;
  isPolling: boolean;
}

function buildControlValues(
  controlVals: Record<string, unknown>
): ControlValues | undefined {
  if (Object.keys(controlVals).length === 0) return undefined;

  const cv: ControlValues = {};
  for (const [, value] of Object.entries(controlVals)) {
    if (
      value &&
      typeof value === 'object' &&
      'type' in (value as Record<string, unknown>)
    ) {
      const tr = value as Record<string, unknown>;
      const trType = tr.type as string;
      if (['last_n_days', 'last_n_weeks', 'specific_range'].includes(trType)) {
        cv.time_range = value as ControlValues['time_range'];
      }
    }
  }
  return Object.keys(cv).length > 0 ? cv : undefined;
}

function sortBlocksByPosition(blocks: DashboardBlock[]): DashboardBlock[] {
  return [...blocks].sort((a, b) => {
    if (a.position.y !== b.position.y) return a.position.y - b.position.y;
    return a.position.x - b.position.x;
  });
}

export function useDashboardViewer(
  dashboardId: string | undefined
): UseDashboardViewerReturn {
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [blocks, setBlocks] = useState<DashboardBlock[]>([]);
  const [controls, setControls] = useState<DashboardControl[]>([]);
  const [blockData, setBlockData] = useState<
    Map<string, Record<string, unknown>[]>
  >(new Map());
  const [activeControlValues, setActiveControlValues] = useState<
    Record<string, unknown>
  >({});
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const renderHandles = useRef<Map<string, RenderHandle>>(new Map());
  const dashboardIdRef = useRef(dashboardId);
  dashboardIdRef.current = dashboardId;

  useEffect(() => {
    if (!dashboardId) return;

    let cancelled = false;

    async function load(): Promise<void> {
      setIsLoading(true);
      setError(null);

      try {
        const result = await dashboardApi.getById(dashboardId!);
        if (cancelled) return;

        const dash = result.data;
        setDashboard(dash);

        const accepted = sortBlocksByPosition(
          dash.blocks.filter((b) => b.status === 'accepted')
        );
        setBlocks(accepted);
        setControls(
          [...dash.controls].sort((a, b) => a.position - b.position)
        );

        if (accepted.length > 0) {
          try {
            const dataResult = await dashboardApi.fetchAllData(dashboardId!);
            if (cancelled) return;

            const newData = new Map<string, Record<string, unknown>[]>();
            if (dataResult.data && Array.isArray(dataResult.data)) {
              for (const item of dataResult.data) {
                if (item.success && item.data) {
                  newData.set(item.block_id, item.data);
                }
              }
            }
            setBlockData(newData);
          } catch {
            // Non-fatal: blocks render without data
          }
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : 'Failed to load dashboard'
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [dashboardId]);

  const refreshData = useCallback(async () => {
    const id = dashboardIdRef.current;
    if (!id) return;

    setIsRefreshing(true);
    try {
      const cv = buildControlValues(activeControlValues);
      const result = await dashboardApi.fetchAllData(id, cv);
      const newData = new Map<string, Record<string, unknown>[]>();
      if (result.data && Array.isArray(result.data)) {
        for (const item of result.data) {
          if (item.success && item.data) {
            newData.set(item.block_id, item.data);
          }
        }
      }
      setBlockData((prev) => new Map([...prev, ...newData]));
    } catch {
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  }, [activeControlValues]);

  const { lastRefreshedAt, isPolling } = useLivePolling({
    dataMode: dashboard?.data_mode,
    refreshIntervalSeconds: dashboard?.refresh_interval,
    refreshFn: refreshData,
    enabled: !isLoading && blocks.length > 0,
  });

  const changeControlValue = useCallback(
    async (parameterKey: string, value: unknown) => {
      const id = dashboardIdRef.current;
      if (!id) return;

      const updatedValues = { ...activeControlValues, [parameterKey]: value };
      setActiveControlValues(updatedValues);
      setIsRefreshing(true);

      try {
        const cv = buildControlValues(updatedValues);
        const result = await dashboardApi.fetchAllData(id, cv);
        const newData = new Map<string, Record<string, unknown>[]>();
        if (result.data && Array.isArray(result.data)) {
          for (const item of result.data) {
            if (item.success && item.data) {
              newData.set(item.block_id, item.data);
            }
          }
        }
        setBlockData((prev) => new Map([...prev, ...newData]));
      } catch {
        toast.error('Failed to refresh data with new control values');
      } finally {
        setIsRefreshing(false);
      }
    },
    [activeControlValues]
  );

  return {
    dashboard,
    blocks,
    blockData,
    controls,
    activeControlValues,
    isLoading,
    isRefreshing,
    error,
    renderHandles,
    changeControlValue,
    refreshData,
    lastRefreshedAt,
    isPolling,
  };
}
